import { isNumber } from 'lodash';
import React from 'react';
import {
  LayoutChangeEvent,
  View as RNView,
  StyleProp,
  ViewProperties,
  ViewStyle,
} from 'react-native';
import { SharedStyles, grid } from '@shared/styles';
import { Styles } from './styles';

export type MBViewPadding = number;

export interface MBViewRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface MBViewProps {
  /*
        Make the view match its parent's width/height
    */
  fill?: boolean;

  /*
        100% width
    */
  fullWidth?: boolean;

  /*
        100% height
    */
  fullHeight?: boolean;

  /*
        Center align children vertically and horizontally
    */
  center?: boolean;

  /*
        Center aligns children vertically
    */
  centerVertically?: boolean;

  /*
        Center aligns children horizontally
    */
  centerHorizontally?: boolean;

  /*
        Make the view absolutely positioned to cover its parent
    */
  absolute?: boolean;

  /*
        Wraps the views width (for parent flex direction col)
        or height (for parent flex direction row) instead of
        filling the available space.
    */
  wrap?: boolean;

  /*
        Easy to use props to give views standardized paddings
    */
  padding?: MBViewPadding;
  paddingTop?: MBViewPadding;
  paddingLeft?: MBViewPadding;
  paddingRight?: MBViewPadding;
  paddingBottom?: MBViewPadding;
  paddingVertical?: MBViewPadding;
  paddingHorizontal?: MBViewPadding;

  /*
        Flex direction row makes children align in rows. If set to "reverse",
        it will set flex direction to row-reverse
    */
  row?: boolean | 'reverse';

  /*
        Set pointerEvents to "none" to block all the view's touch events
    */
  pointerEvents?: ViewProperties['pointerEvents'];

  /*
        How much should the view flex. If true, flex = 1
        Handy when having to show multiple views inside the
        same parent with different % of the height
    */
  flex?: boolean | number;
  /*
        Get the size of the component. Called when the view's layout changes.
    */
  onSize?: (size: MBViewRect) => void;

  /*
        CSS classes
    */
  class?: string;

  style?: StyleProp<ViewStyle>;
  children?: React.ReactNode | React.ReactNode[];
  testID?: string;
  /*
       Used to give DOM elements ID on web
    */
  nativeID?: string;
}

// Note(fant): the reason we've done MBView as a component and not an SFC is because
// we really want to avoid providing arrow functions as props since that would cause
// the components and children to re-render every time some prop is changed. This
// is really important for performance reasons. In other parts of the shared components
// we're a little sloppy with this, which is not great. But for these most fundamental
// components it's crucial.
export class MBView extends React.PureComponent<MBViewProps> {
  onSize = (event: LayoutChangeEvent) => {
    // RN View's LayoutRectangle is typed as { x, y, width, height } but in
    // practice, its x/y values are 0, and it includes left/right values which
    // are correct. So casting as unknown as MBViewSize here so callers can get
    // the coords they need without having to untangle this inconsistency.
    this.props.onSize?.(event.nativeEvent.layout as unknown as MBViewRect);
  };

  render() {
    const {
      testID,
      nativeID,
      row,
      wrap,
      fill,
      center,
      absolute,
      fullWidth,
      fullHeight,
      flex,
      centerVertically,
      centerHorizontally,
      padding,
      paddingTop,
      paddingLeft,
      paddingRight,
      paddingBottom,
      paddingVertical,
      paddingHorizontal,
      style,
      onSize,
      pointerEvents,
      children,
    } = this.props;
    return (
      <RNView
        testID={testID}
        nativeID={nativeID}
        style={[
          // Default the flex (otherwise it's weird on web)
          // Reset zIndex, otherwise it defaults to 0, which
          // creates trouble.
          { flex: null },
          // react-native-web gives all views z-index 0 as default.
          // This causes issues when trying to overlay content in nested
          // views. Resetting to no z-index makes the HTML renderer
          // figure out the view hierarchy better.
          { zIndex: 'unset' },
          row === true && Styles.row,
          row === 'reverse' && Styles.rowReverse,
          wrap && Styles.wrap,
          fill && SharedStyles.fill,
          center && SharedStyles.centerChildren,
          absolute && SharedStyles.absoluteCover,
          fullWidth && { width: '100%' },
          fullHeight && { height: '100%' },
          // if flex is a bool: Number(true) => 1
          // if flex is a number: Number(val) => val
          !!flex && { flex: Number(flex) },

          !row && centerVertically && { justifyContent: 'center' },
          !row && centerHorizontally && { alignItems: 'center' },
          row && centerVertically && { alignItems: 'center' },
          row && centerHorizontally && { justifyContent: 'center' },

          isNumber(padding) && { padding: grid(padding) },
          isNumber(paddingTop) && { paddingTop: grid(paddingTop) },
          isNumber(paddingLeft) && { paddingLeft: grid(paddingLeft) },
          isNumber(paddingRight) && {
            paddingRight: grid(paddingRight),
          },
          isNumber(paddingBottom) && {
            paddingBottom: grid(paddingBottom),
          },
          isNumber(paddingVertical) && {
            paddingTop: grid(paddingVertical),
            paddingBottom: grid(paddingVertical),
          },
          isNumber(paddingHorizontal) && {
            paddingLeft: grid(paddingHorizontal),
            paddingRight: grid(paddingHorizontal),
          },

          style,
        ]}
        onLayout={!!onSize ? this.onSize : undefined}
        pointerEvents={pointerEvents}
        {...({ className: this.props.class } as any)}
      >
        {children}
      </RNView>
    );
  }
}
